import { accountsReducer, selectedAccStateSelector } from '@core/store/library-stores';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { useSetAccDataSource } from '@features/dataSource';
import {
  Button,
  FormGenerator,
  LoadingData,
  useNotifications,
  ErrorResponse,
  isValidationErrorResult,
} from '@hwm/ui-lib';
import { t, Trans } from '@lingui/macro';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useSidebar } from '@shared/hooks/useSider';
import { Formik, FormikProps } from 'formik';
import { isEmpty } from 'lodash';
import React, { useCallback, useRef } from 'react';
import { useLoading } from '../../../hooks/useLoading';
import { SiderKeys } from '../Sidebar.utils';
import {
  CreateEditPMZoneErrorCode,
  CreateEditPMZoneErrorCodeMessage,
  CreatePMZoneFields,
  createPMZoneFormValidationSchemaFn,
  fieldsConfiguration,
  formInitialValues,
  mapFormFieldsToRequest,
  PMZoneModalTypes,
  PMZ_FORM_FIELD_NAMES,
} from './CreateEditZoneForm.utils';
import { useFormSubmit } from './dataSource';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      '& input, textarea': {
        padding: theme.spacing(2),
      },
      '& .MuiOutlinedInput-multiline': {
        padding: 0,
      },
      [theme.breakpoints.down('lg')]: {
        width: '100%',
        minWidth: '100%',
      },
    },
  })
);

interface CreateEditZoneFormProps {
  typeOfForm: PMZoneModalTypes;
  /**
   * `dataTestId` - name of the Create/Edit Zone Form (preferably `<name>-create-edit-zone`)
   * @example
   * ```tsx
   * <CreateEditZoneForm {...props} dataTestId="test-create-edit-zone" />
   */
  dataTestId: string;
}

const CONTEXT = 'PMZ';
const SAVE_DELAY = 2000;

export const CreateEditZoneForm: React.FC<CreateEditZoneFormProps> = ({ typeOfForm, dataTestId }) => {
  const { canEditOrCreate } = usePermissions();

  const dispatch = useAppDispatch();

  const { handleError, handleSuccess } = useNotifications();

  const createForm = typeOfForm === 'create';

  const { selectedAccount, hierarchy } = useAppSelector(selectedAccStateSelector);
  const { setAccData } = useSetAccDataSource();

  const { sendFormData, submittingForm } = useFormSubmit(selectedAccount?.id, createForm);
  const formikRef = useRef<FormikProps<CreatePMZoneFields>>(null);

  const { handleSidebarSection } = useSidebar();
  const [loadingState] = useLoading(submittingForm, 3000);

  const formSubmitHandler = useCallback(
    async (formikRef: React.MutableRefObject<FormikProps<CreatePMZoneFields>>) => {
      const formik = formikRef.current;
      formik.submitForm().then(() => {
        const values = formik.values;
        formik.validateForm(values).then((validationErrors) => {
          if (isEmpty(validationErrors)) {
            const PMZoneFormData = mapFormFieldsToRequest(values);

            sendFormData({ body: PMZoneFormData })
              .then((pmzId) => {
                if (createForm) {
                  handleSuccess(`${t`PMZ created`}`, CONTEXT);
                  if (Number.isFinite(pmzId)) {
                    setTimeout(() => {
                      setAccData({ accountId: pmzId?.toString() });
                      handleSidebarSection(SiderKeys.pmzDetails);
                    }, SAVE_DELAY);
                  } else {
                    handleSidebarSection(SiderKeys.accHierarchy);
                  }
                } else {
                  setTimeout(() => {
                    dispatch(
                      accountsReducer.slice.actions.setSelectedAccount({
                        account: {
                          ...selectedAccount,
                          name: values.name,
                          description: values.description,
                        },
                      })
                    );
                    handleSidebarSection(SiderKeys.pmzDetails);
                    handleSuccess(`${t`PMZ edited`}`, CONTEXT);
                  }, SAVE_DELAY);
                }
              })
              .catch((err: ErrorResponse) => {
                if (isValidationErrorResult(err)) {
                  const errors = err?.data?.flatMap((d) =>
                    d?.errors?.map((err) => err?.code as CreateEditPMZoneErrorCode)
                  );

                  const errMsgs = errors
                    ?.map((err) => CreateEditPMZoneErrorCodeMessage?.[err] ?? null)
                    .filter(Boolean)
                    .join(', ');

                  if (Boolean(errors?.find((errCode) => errCode === CreateEditPMZoneErrorCode.AccountNameNotUnique))) {
                    formik?.setFieldError(PMZ_FORM_FIELD_NAMES.accNameField, t`Account name should be unique`);
                  }
                  handleError(errMsgs, CONTEXT);
                } else {
                  const errMsg = `${t`There was a problem with`} ${createForm ? t`creating` : t`editing`} ${t`PMZ`}`;
                  handleError(errMsg, CONTEXT);
                }
              });
          }
        });
      });
    },
    [createForm, selectedAccount, dispatch, setAccData, handleError, sendFormData, handleSuccess, handleSidebarSection]
  );

  return (
    <div
      className="flex flex-col justify-between h-full max-w-full w-full overflow-y-auto"
      data-testid={`${dataTestId}-wrapper`}
    >
      <Formik
        enableReinitialize
        validationSchema={createPMZoneFormValidationSchemaFn()}
        initialValues={formInitialValues(typeOfForm, selectedAccount, hierarchy)}
        onSubmit={(values, obj) => {}}
        innerRef={formikRef}
      >
        {(formikProps: FormikProps<CreatePMZoneFields>) => {
          if (!formikProps.values) return <></>;

          return loadingState ? (
            <LoadingData dataTestId={`${dataTestId}-loading`} />
          ) : (
            <form
              className="overflow-x-hidden max-w-full mx-2 mb-2"
              onSubmit={formikProps.handleSubmit}
              data-testid={`${dataTestId}-form`}
            >
              <FormGenerator
                {...formikProps}
                fieldsConfiguration={fieldsConfiguration(canEditOrCreate)}
                formDisabled={false}
                dataTestId={`${dataTestId}-form`}
              />
            </form>
          );
        }}
      </Formik>
      <Button
        className="self-end"
        style={{ height: 36, minHeight: 36, width: 120, marginBottom: 2 }}
        loading={loadingState}
        disabled={loadingState || !canEditOrCreate}
        onClick={() => formSubmitHandler(formikRef)}
        dataTestId={`${dataTestId}-save-button`}
      >
        <Trans id="[Action.Save]" comment="Save as verb">
          Save
        </Trans>
      </Button>
    </div>
  );
};
